import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import {
  RangoBrazo,
  ModificadoresBrazo,
  calcularPuntuacionBrazo,
  RangoAntebrazo,
  ModificadorAntebrazo,
  calcularPuntuacionAntebrazo,
  RangoMuneca,
  ModificadorMuneca,
  calcularPuntuacionMuneca,
  RangoGiroMuneca,
  calcularPuntuacionGiroMuneca,
  calcularPuntuacionGrupoA,
  RangoCuello,
  ModificadoresCuello,
  calcularPuntuacionCuello,
  RangoTronco,
  ModificadoresTronco,
  calcularPuntuacionTronco,
  CondicionPiernas,
  calcularPuntuacionPiernas,
  calcularPuntuacionGrupoB,
  TipoActividad,
  CargaFuerza,
  calcularPuntuacionC,
  calcularPuntuacionD,
  calcularPuntuacionFinalRula,
  obtenerNivelActuacion,
} from '../lib/rula';
import {
  createRulaAssessment,
  fetchRulaAssessments,
  updateRulaAssessment,
  deleteRulaAssessment,
  RulaAssessmentRow,
  RulaAssessmentResultado,
} from '../lib/gestion-tecnica-rula';
import { generateRulaReportPDF } from '../lib/gestion-tecnica-rula-pdf';
import { fetchJobPositionsFull, JobPositionLegalFields } from '../lib/gestion-tecnica-constructor';
import {
  RulaBrazoDiagram,
  RulaAntebrazoDiagram,
  RulaMunecaDiagram,
  RulaGiroMunecaDiagram,
  RulaCuelloDiagram,
  RulaTroncoDiagram,
  RulaPiernasDiagram,
} from '../components/rula/RulaBodyDiagrams';

// Mismos estilos/paleta que RuidoMethodologyPage.tsx / IluminacionMethodologyPage.tsx
// (fondo oscuro, números principales en 34px).
const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600, fontSize: 13,
};

const cardStyle: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 20,
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', color: '#e2e8f0', fontSize: 13,
};

// Descripción + diagrama de referencia por sección (ver src/components/rula/RulaBodyDiagrams.tsx).
const fieldDescStyle: React.CSSProperties = {
  fontSize: 12.5, color: '#94a3b8', margin: '0 0 8px 0', lineHeight: 1.45,
};
const diagramBoxStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', background: '#0b1220', border: '1px solid #1e293b',
  borderRadius: 8, padding: 10, marginBottom: 12,
};

// Colores por nivel de actuación (Tabla 18 RULA): 1=verde, 2=ámbar, 3=naranja, 4=rojo.
const NIVEL_COLORES: Record<number, { bg: string; border: string; text: string }> = {
  1: { bg: 'rgba(47,191,113,0.12)', border: 'rgba(47,191,113,0.3)', text: '#2FBF71' },
  2: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', text: '#f59e0b' },
  3: { bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.3)', text: '#f97316' },
  4: { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
};

function ResultadoIntermedio({ label, valor }: { label: string; valor: number }) {
  return (
    <div>
      <span style={{ color: '#94a3b8', fontSize: 12 }}>{label}</span>
      <div style={{ fontSize: 20, fontWeight: 600, color: '#e2e8f0' }}>{valor}</div>
    </div>
  );
}

export default function RulaMethodologyPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';
  const isSuperAdmin         = profile?.role === 'superadmin';

  // ── Plan gate (after all hooks, mismo criterio que Ruido/Iluminación) ──
  const planAllowed = isSuperAdmin || !displayCompany || hasPlanAccess(displayCompany.plan, 'metodologia-rula');

  const [jobPositions, setJobPositions] = useState<JobPositionLegalFields[]>([]);
  const [savedAssessments, setSavedAssessments] = useState<RulaAssessmentRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [descripcion, setDescripcion] = useState('');
  const [selectedJobPositionId, setSelectedJobPositionId] = useState('');
  const [lado, setLado] = useState<'izquierdo' | 'derecho'>('derecho');

  // ── Grupo A: brazo, antebrazo, muñeca, giro de muñeca ──
  // Default = postura neutra completa (caso verificado en rula.ts): todos
  // los insumos en su valor mínimo → Grupo A=1, Grupo B=1, C=1, D=1,
  // puntuación final=1, Nivel 1 "Riesgo Aceptable".
  const [rangoBrazo, setRangoBrazo] = useState<RangoBrazo>('neutro');
  const [modBrazo, setModBrazo] = useState<ModificadoresBrazo>({
    hombroElevadoORotado: false, abducido: false, puntoApoyo: false,
  });

  const [rangoAntebrazo, setRangoAntebrazo] = useState<RangoAntebrazo>('flexion60a100');
  const [modAntebrazo, setModAntebrazo] = useState<ModificadorAntebrazo>('ninguno');

  const [rangoMuneca, setRangoMuneca] = useState<RangoMuneca>('neutra');
  const [modMuneca, setModMuneca] = useState<ModificadorMuneca>('ninguno');

  const [rangoGiroMuneca, setRangoGiroMuneca] = useState<RangoGiroMuneca>('medio');

  // ── Grupo B: cuello, tronco, piernas ──
  const [rangoCuello, setRangoCuello] = useState<RangoCuello>('flexion0a10');
  const [modCuello, setModCuello] = useState<ModificadoresCuello>({
    rotada: false, inclinacionLateral: false,
  });

  const [rangoTronco, setRangoTronco] = useState<RangoTronco>('erguidoOApoyado');
  const [modTronco, setModTronco] = useState<ModificadoresTronco>({
    rotado: false, inclinacionLateral: false,
  });

  const [condicionPiernas, setCondicionPiernas] = useState<CondicionPiernas>('apoyadasYEquilibrado');

  // ── Modificadores finales: actividad y carga/fuerza ──
  const [tipoActividad, setTipoActividad] = useState<TipoActividad>({
    estatica: false, repetitiva: false,
  });
  const [cargaFuerza, setCargaFuerza] = useState<CargaFuerza>('menor2kgIntermitente');

  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [ultimaEvaluacionGuardada, setUltimaEvaluacionGuardada] = useState<RulaAssessmentRow | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!companyId) { setLoadingData(false); return; }
    setLoadingData(true);
    try {
      const [positions, assessments] = await Promise.all([
        fetchJobPositionsFull(companyId),
        fetchRulaAssessments(companyId),
      ]);
      setJobPositions(positions);
      setSavedAssessments(assessments);
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar datos');
    } finally {
      setLoadingData(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ===== Cálculo en vivo (sin botón "Calcular") =====
  const puntuacionBrazo = calcularPuntuacionBrazo(rangoBrazo, modBrazo);
  const puntuacionAntebrazo = calcularPuntuacionAntebrazo(rangoAntebrazo, modAntebrazo);
  const puntuacionMuneca = calcularPuntuacionMuneca(rangoMuneca, modMuneca);
  const puntuacionGiroMuneca = calcularPuntuacionGiroMuneca(rangoGiroMuneca);
  const grupoA = calcularPuntuacionGrupoA(puntuacionBrazo, puntuacionAntebrazo, puntuacionMuneca, puntuacionGiroMuneca);

  const puntuacionCuello = calcularPuntuacionCuello(rangoCuello, modCuello);
  const puntuacionTronco = calcularPuntuacionTronco(rangoTronco, modTronco);
  const puntuacionPiernas = calcularPuntuacionPiernas(condicionPiernas);
  const grupoB = calcularPuntuacionGrupoB(puntuacionCuello, puntuacionTronco, puntuacionPiernas);

  const puntuacionC = calcularPuntuacionC(grupoA, tipoActividad, cargaFuerza);
  const puntuacionD = calcularPuntuacionD(grupoB, tipoActividad, cargaFuerza);
  const puntuacionFinal = calcularPuntuacionFinalRula(puntuacionC, puntuacionD);
  const nivel = obtenerNivelActuacion(puntuacionFinal);
  const colores = NIVEL_COLORES[nivel.nivel];

  const puedeGuardar = descripcion.trim().length > 0;

  const handleGuardar = async () => {
    setError(null);
    if (!descripcion.trim()) { setError('Ingresa una descripción del local/puesto/tarea evaluada'); return; }

    setGuardando(true);
    setGuardadoOk(false);
    try {
      const resultado: RulaAssessmentResultado = {
        puntuacionBrazo, puntuacionAntebrazo, puntuacionMuneca, puntuacionGiroMuneca, grupoA,
        puntuacionCuello, puntuacionTronco, puntuacionPiernas, grupoB,
        puntuacionC, puntuacionD, puntuacionFinal, nivelActuacion: nivel,
      };

      const payload = {
        companyId,
        jobPositionId: selectedJobPositionId || undefined,
        descripcion,
        lado,
        inputs: {
          rangoBrazo, modBrazo, rangoAntebrazo, modAntebrazo, rangoMuneca, modMuneca, rangoGiroMuneca,
          rangoCuello, modCuello, rangoTronco, modTronco, condicionPiernas, tipoActividad, cargaFuerza,
        },
        resultado,
        createdBy: profile?.id ?? null,
      };

      if (editingId) {
        await updateRulaAssessment(editingId, payload);
      } else {
        await createRulaAssessment(payload);
      }
      // createRulaAssessment() no retorna el registro insertado (mismo patrón
      // que createNoiseCalculation()) — se vuelve a consultar para obtener el
      // registro recién creado con su id real, necesario para el PDF.
      const assessments = await fetchRulaAssessments(companyId);
      setSavedAssessments(assessments);
      setUltimaEvaluacionGuardada(assessments[0] ?? null);
      setGuardadoOk(true);
      setEditingId(null);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar la evaluación');
    } finally {
      setGuardando(false);
    }
  };

  function handleEditar(a: RulaAssessmentRow) {
    setEditingId(a.id);
    setDescripcion(a.descripcion);
    setLado(a.lado);
    setSelectedJobPositionId(a.job_position_id ?? '');
    const i = a.inputs;
    setRangoBrazo(i.rangoBrazo);
    setModBrazo(i.modBrazo);
    setRangoAntebrazo(i.rangoAntebrazo);
    setModAntebrazo(i.modAntebrazo);
    setRangoMuneca(i.rangoMuneca);
    setModMuneca(i.modMuneca);
    setRangoGiroMuneca(i.rangoGiroMuneca);
    setRangoCuello(i.rangoCuello);
    setModCuello(i.modCuello);
    setRangoTronco(i.rangoTronco);
    setModTronco(i.modTronco);
    setCondicionPiernas(i.condicionPiernas);
    setTipoActividad(i.tipoActividad);
    setCargaFuerza(i.cargaFuerza);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleEliminar(id: string) {
    if (!window.confirm('¿Eliminar esta evaluación? Esta acción no se puede deshacer.')) return;
    setDeletingId(id);
    await deleteRulaAssessment(id);
    const updated = await fetchRulaAssessments(companyId);
    setSavedAssessments(updated);
    setDeletingId(null);
  }

  const handleDescargarPdf = async () => {
    if (!ultimaEvaluacionGuardada) {
      setError('No hay una evaluación guardada para generar el PDF');
      return;
    }
    setDescargandoPdf(true);
    try {
      await generateRulaReportPDF(
        ultimaEvaluacionGuardada,
        companyId,
        displayCompany?.name ?? 'Empresa',
        profile?.id ?? null
      );
    } catch (e: any) {
      setError(e.message ?? 'Error al generar el PDF');
    } finally {
      setDescargandoPdf(false);
    }
  };

  if (!planAllowed) {
    return <UpgradeWall {...PLAN_FEATURES['metodologia-rula']} />;
  }

  if (loadingData) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Higiene y Gestión del Riesgo Ergonómico — Método RULA</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Rapid Upper Limb Assessment. Fuente: McAtamney, L. &amp; Corlett, E.N. (1993). RULA: a survey method for the investigation of work-related upper limb disorders. Applied Ergonomics, 24(2), 91-99.
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setLado('izquierdo')}
          style={{
            background: lado === 'izquierdo' ? '#334155' : 'transparent', color: '#e2e8f0', border: '1px solid #334155',
            borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
          }}
        >
          Lado Izquierdo
        </button>
        <button
          onClick={() => setLado('derecho')}
          style={{
            background: lado === 'derecho' ? '#334155' : 'transparent', color: '#e2e8f0', border: '1px solid #334155',
            borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
          }}
        >
          Lado Derecho
        </button>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Descripción (local, puesto o tarea evaluada)</label>
        <input
          style={inputStyle}
          type="text"
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder="Ej. Operador de ensamblaje, Digitador..."
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Puesto de trabajo (opcional)</label>
        <SelectBuscable inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Puesto de trabajo (opcional)" value={selectedJobPositionId}
          onChange={setSelectedJobPositionId} vacio="— Sin asociar —" opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))} />
      </div>

      {/* ===== GRUPO A: miembros superiores ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Grupo A — Miembros superiores</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Brazo</label>
          <p style={fieldDescStyle}>
            Grado de flexión/extensión del brazo, medido entre el eje del brazo (hombro–codo) y el eje del tronco.
          </p>
          <div style={diagramBoxStyle}>
            <RulaBrazoDiagram rango={rangoBrazo} mod={modBrazo} />
          </div>
          <select style={inputStyle} value={rangoBrazo} onChange={e => setRangoBrazo(e.target.value as RangoBrazo)}>
            <option value="neutro" style={{ color: '#000' }}>Neutro (20° extensión a 20° flexión)</option>
            <option value="extension20_o_flexion20a45" style={{ color: '#000' }}>Extensión &gt;20° o flexión 20°-45°</option>
            <option value="flexion45a90" style={{ color: '#000' }}>Flexión 45°-90°</option>
            <option value="flexionMayor90" style={{ color: '#000' }}>Flexión &gt;90°</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={modBrazo.hombroElevadoORotado}
                onChange={e => setModBrazo(prev => ({ ...prev, hombroElevadoORotado: e.target.checked }))} />
              Hombro elevado o brazo rotado (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={modBrazo.abducido}
                onChange={e => setModBrazo(prev => ({ ...prev, abducido: e.target.checked }))} />
              Brazo abducido (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={modBrazo.puntoApoyo}
                onChange={e => setModBrazo(prev => ({ ...prev, puntoApoyo: e.target.checked }))} />
              Existe punto de apoyo (-1)
            </label>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Antebrazo</label>
          <p style={fieldDescStyle}>
            Grado de flexión del antebrazo, medido entre el eje del antebrazo (codo–muñeca) y el eje del brazo (hombro–codo).
          </p>
          <div style={diagramBoxStyle}>
            <RulaAntebrazoDiagram rango={rangoAntebrazo} mod={modAntebrazo} />
          </div>
          <select style={inputStyle} value={rangoAntebrazo} onChange={e => setRangoAntebrazo(e.target.value as RangoAntebrazo)}>
            <option value="flexion60a100" style={{ color: '#000' }}>Flexión 60°-100°</option>
            <option value="fueraDe60a100" style={{ color: '#000' }}>Flexión &lt;60° o &gt;100°</option>
          </select>
          <div style={{ marginTop: 8, display: 'flex', gap: 16 }}>
            {([
              { value: 'ninguno', label: 'Ninguno' },
              { value: 'aLadoDelCuerpo', label: 'A un lado del cuerpo (+1)' },
              { value: 'cruzaLineaMedia', label: 'Cruza línea media (+1)' },
            ] as { value: ModificadorAntebrazo; label: string }[]).map(opt => (
              <label key={opt.value} style={checkboxLabelStyle}>
                <input type="radio" name="modAntebrazo" checked={modAntebrazo === opt.value}
                  onChange={() => setModAntebrazo(opt.value)} />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Muñeca</label>
          <p style={fieldDescStyle}>
            Grado de flexión/extensión de la muñeca, medido entre el eje de la muñeca (mano) y el eje del antebrazo.
          </p>
          <div style={diagramBoxStyle}>
            <RulaMunecaDiagram rango={rangoMuneca} mod={modMuneca} />
          </div>
          <select style={inputStyle} value={rangoMuneca} onChange={e => setRangoMuneca(e.target.value as RangoMuneca)}>
            <option value="neutra" style={{ color: '#000' }}>Posición neutra</option>
            <option value="flexionExtension0a15" style={{ color: '#000' }}>Flexión/extensión &gt;0° y &lt;15°</option>
            <option value="flexionExtensionMayor15" style={{ color: '#000' }}>Flexión/extensión &gt;15°</option>
          </select>
          <div style={{ marginTop: 8, display: 'flex', gap: 16 }}>
            {([
              { value: 'ninguno', label: 'Ninguna' },
              { value: 'desviacionRadial', label: 'Desviación radial (+1)' },
              { value: 'desviacionCubital', label: 'Desviación cubital (+1)' },
            ] as { value: ModificadorMuneca; label: string }[]).map(opt => (
              <label key={opt.value} style={checkboxLabelStyle}>
                <input type="radio" name="modMuneca" checked={modMuneca === opt.value}
                  onChange={() => setModMuneca(opt.value)} />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label style={labelStyle}>Giro de muñeca</label>
          <p style={fieldDescStyle}>
            Grado de pronación o supinación (giro del antebrazo sobre su propio eje longitudinal).
          </p>
          <div style={diagramBoxStyle}>
            <RulaGiroMunecaDiagram rango={rangoGiroMuneca} />
          </div>
          <select style={inputStyle} value={rangoGiroMuneca} onChange={e => setRangoGiroMuneca(e.target.value as RangoGiroMuneca)}>
            <option value="medio" style={{ color: '#000' }}>Pronación o supinación media</option>
            <option value="extremo" style={{ color: '#000' }}>Pronación o supinación extrema</option>
          </select>
        </div>

        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <ResultadoIntermedio label="Brazo" valor={puntuacionBrazo} />
          <ResultadoIntermedio label="Antebrazo" valor={puntuacionAntebrazo} />
          <ResultadoIntermedio label="Muñeca" valor={puntuacionMuneca} />
          <ResultadoIntermedio label="Giro" valor={puntuacionGiroMuneca} />
        </div>
      </div>

      {/* ===== GRUPO B: cuello, tronco, piernas ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Grupo B — Cuello, tronco y piernas</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Cuello</label>
          <p style={fieldDescStyle}>
            Grado de flexión (o extensión) del cuello, medido entre el eje de la cabeza/cuello y el eje del tronco.
          </p>
          <div style={diagramBoxStyle}>
            <RulaCuelloDiagram rango={rangoCuello} mod={modCuello} />
          </div>
          <select style={inputStyle} value={rangoCuello} onChange={e => setRangoCuello(e.target.value as RangoCuello)}>
            <option value="flexion0a10" style={{ color: '#000' }}>Flexión 0°-10°</option>
            <option value="flexion10a20" style={{ color: '#000' }}>Flexión &gt;10° y ≤20°</option>
            <option value="flexionMayor20" style={{ color: '#000' }}>Flexión &gt;20°</option>
            <option value="extension" style={{ color: '#000' }}>Extensión (cualquier grado)</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={modCuello.rotada}
                onChange={e => setModCuello(prev => ({ ...prev, rotada: e.target.checked }))} />
              Cabeza rotada (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={modCuello.inclinacionLateral}
                onChange={e => setModCuello(prev => ({ ...prev, inclinacionLateral: e.target.checked }))} />
              Inclinación lateral (+1)
            </label>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Tronco</label>
          <p style={fieldDescStyle}>
            Grado de flexión del tronco respecto a la vertical, medido entre el eje del tronco y la referencia vertical fija.
          </p>
          <div style={diagramBoxStyle}>
            <RulaTroncoDiagram rango={rangoTronco} mod={modTronco} />
          </div>
          <select style={inputStyle} value={rangoTronco} onChange={e => setRangoTronco(e.target.value as RangoTronco)}>
            <option value="erguidoOApoyado" style={{ color: '#000' }}>Erguido / sentado bien apoyado (ángulo tronco-piernas &gt;90°)</option>
            <option value="flexion0a20" style={{ color: '#000' }}>Flexión &gt;0° y ≤20°</option>
            <option value="flexion20a60" style={{ color: '#000' }}>Flexión &gt;20° y ≤60°</option>
            <option value="flexionMayor60" style={{ color: '#000' }}>Flexión &gt;60°</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={modTronco.rotado}
                onChange={e => setModTronco(prev => ({ ...prev, rotado: e.target.checked }))} />
              Tronco rotado (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={modTronco.inclinacionLateral}
                onChange={e => setModTronco(prev => ({ ...prev, inclinacionLateral: e.target.checked }))} />
              Inclinación lateral (+1)
            </label>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Piernas</label>
          <p style={fieldDescStyle}>
            Condición de apoyo de las piernas: si están bien apoyadas con el peso distribuido de forma simétrica, o no.
          </p>
          <div style={diagramBoxStyle}>
            <RulaPiernasDiagram condicion={condicionPiernas} />
          </div>
          <select style={inputStyle} value={condicionPiernas} onChange={e => setCondicionPiernas(e.target.value as CondicionPiernas)}>
            <option value="apoyadasYEquilibrado" style={{ color: '#000' }}>Bien apoyadas / peso simétrico con espacio para cambiar</option>
            <option value="noApoyadasOAsimetrico" style={{ color: '#000' }}>No apoyadas o peso no simétrico</option>
          </select>
        </div>

        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <ResultadoIntermedio label="Cuello" valor={puntuacionCuello} />
          <ResultadoIntermedio label="Tronco" valor={puntuacionTronco} />
          <ResultadoIntermedio label="Piernas" valor={puntuacionPiernas} />
        </div>
      </div>

      {/* ===== Modificadores finales: actividad y carga/fuerza ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Actividad y carga/fuerza</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Tipo de actividad</label>
          <label style={checkboxLabelStyle}>
            <input type="checkbox" checked={tipoActividad.estatica}
              onChange={e => setTipoActividad(prev => ({ ...prev, estatica: e.target.checked }))} />
            Estática — postura mantenida &gt;1 minuto seguido (+1)
          </label>
          <label style={checkboxLabelStyle}>
            <input type="checkbox" checked={tipoActividad.repetitiva}
              onChange={e => setTipoActividad(prev => ({ ...prev, repetitiva: e.target.checked }))} />
            Repetitiva — se repite &gt;4 veces/minuto (+1)
          </label>
        </div>

        <div>
          <label style={labelStyle}>Carga o fuerza aplicada</label>
          <select style={inputStyle} value={cargaFuerza} onChange={e => setCargaFuerza(e.target.value as CargaFuerza)}>
            <option value="menor2kgIntermitente" style={{ color: '#000' }}>Carga &lt;2kg intermitente (0)</option>
            <option value="de2a10kgIntermitente" style={{ color: '#000' }}>Carga 2-10kg intermitente (+1)</option>
            <option value="de2a10kgEstaticaORepetitiva" style={{ color: '#000' }}>Carga 2-10kg estática o repetitiva (+2)</option>
            <option value="mayor10kgIntermitente" style={{ color: '#000' }}>Carga &gt;10kg intermitente (+2)</option>
            <option value="mayor10kgEstaticaORepetitiva" style={{ color: '#000' }}>Carga &gt;10kg estática o repetitiva (+3)</option>
            <option value="golpesOFuerzasBruscas" style={{ color: '#000' }}>Golpes o fuerzas bruscas (+3)</option>
          </select>
        </div>
      </div>

      {/* ===== Resultado ===== */}
      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
          <ResultadoIntermedio label="Puntuación Grupo A" valor={grupoA} />
          <ResultadoIntermedio label="Puntuación Grupo B" valor={grupoB} />
          <ResultadoIntermedio label="Puntuación C" valor={puntuacionC} />
          <ResultadoIntermedio label="Puntuación D" valor={puntuacionD} />
        </div>

        <span style={{ color: '#94a3b8', fontSize: 12 }}>Puntuación Final RULA</span>
        <div style={{ fontSize: 34, fontWeight: 600, color: '#e2e8f0', marginBottom: 14 }}>{puntuacionFinal}</div>

        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
            background: colores.bg, border: `1px solid ${colores.border}`,
          }}
        >
          <strong style={{ color: colores.text, fontSize: 14 }}>Nivel {nivel.nivel}</strong>
          <span style={{ color: '#94a3b8', fontSize: 12 }}>— {nivel.descripcion}</span>
        </div>
      </div>

      <button
        onClick={handleGuardar}
        disabled={guardando || !puedeGuardar}
        style={{
          background: '#2FBF71', color: '#0f172a', border: 'none', borderRadius: 6,
          padding: '8px 16px', fontWeight: 600, cursor: guardando || !puedeGuardar ? 'not-allowed' : 'pointer',
          opacity: guardando || !puedeGuardar ? 0.6 : 1,
        }}
      >
        {guardando ? 'Guardando...' : 'Guardar evaluación'}
      </button>

      {guardadoOk && (
        <div style={{ color: '#2FBF71', fontSize: 13, marginTop: 10 }}>✓ Evaluación guardada correctamente</div>
      )}

      {guardadoOk && ultimaEvaluacionGuardada && (
        <button
          onClick={handleDescargarPdf}
          disabled={descargandoPdf}
          style={{
            background: '#3b82f6', color: '#0f172a', border: 'none', borderRadius: 6,
            padding: '8px 16px', fontWeight: 600, cursor: descargandoPdf ? 'not-allowed' : 'pointer',
            opacity: descargandoPdf ? 0.6 : 1, marginTop: 10, marginLeft: 10,
          }}
        >
          {descargandoPdf ? 'Generando PDF...' : 'Descargar reporte PDF'}
        </button>
      )}

      {savedAssessments.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 8 }}>Evaluaciones guardadas</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Descripción</th>
                <th style={{ padding: '6px 8px' }}>Lado</th>
                <th style={{ padding: '6px 8px' }}>Puntuación Final</th>
                <th style={{ padding: '6px 8px' }}>Nivel</th>
                <th style={{ padding: '6px 8px' }}>Fecha</th>
                <th style={{ padding: '6px 8px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {savedAssessments.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '6px 8px' }}>{a.descripcion}</td>
                  <td style={{ padding: '6px 8px' }}>{a.lado === 'izquierdo' ? 'Izquierdo' : 'Derecho'}</td>
                  <td style={{ padding: '6px 8px' }}>{a.resultado.puntuacionFinal}</td>
                  <td style={{ padding: '6px 8px' }}>{a.resultado.nivelActuacion.nivel}</td>
                  <td style={{ padding: '6px 8px' }}>{new Date(a.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: '6px 8px', display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => generateRulaReportPDF(a, companyId, displayCompany?.name ?? 'Empresa', profile?.id ?? null)}
                      style={{ background: 'transparent', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}
                    >
                      Descargar
                    </button>
                    <button
                      onClick={() => handleEditar(a)}
                      style={{ background: 'transparent', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleEliminar(a.id)}
                      disabled={deletingId === a.id}
                      style={{
                        background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 4,
                        padding: '3px 8px', fontSize: 11, cursor: deletingId === a.id ? 'not-allowed' : 'pointer',
                        opacity: deletingId === a.id ? 0.6 : 1,
                      }}
                    >
                      {deletingId === a.id ? 'Eliminando…' : 'Eliminar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
